import { lazy, Suspense, useId, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { ProductImage } from './ProductImage';
import {
    type ImageBackgroundColorValue,
    type OnClick,
    type SrcSet,
    type TileSize,
} from '../constants';

import { type ProductCarouselItem_viewer$key } from './__generated__/ProductCarouselItem_viewer.graphql';
import { type ProductCarouselItem_item$key } from './__generated__/ProductCarouselItem_item.graphql';
import { type ProductCarouselItem_photos$key } from './__generated__/ProductCarouselItem_photos.graphql';

import dibsCss from 'dibs-css';

const LazyProductVideo = lazy(() => import('./ProductVideo'));

const viewerFragment = graphql`
    fragment ProductCarouselItem_viewer on Viewer {
        ...ProductVideo_viewer
    }
`;

const itemFragment = graphql`
    fragment ProductCarouselItem_item on Item
    @argumentDefinitions(fetchVideo: { type: "Boolean", defaultValue: false }) {
        hasApprovedVideo @include(if: $fetchVideo)
        ...ProductImage_item
        ...ProductVideo_item
    }
`;

const photoFragment = graphql`
    fragment ProductCarouselItem_photos on ItemPhoto {
        ...ProductImage_photo
    }
`;

const TileSpinner: FC<{ backgroundColor?: ImageBackgroundColorValue }> = ({ backgroundColor }) => {
    return (
        <div
            className={classnames(
                dibsCss.relative,
                dibsCss.wFull,
                dibsCss.hFull,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                backgroundColor
            )}
        >
            <Spinner black containerClass={dibsCss.absolute} />
        </div>
    );
};

type Props = {
    viewer: ProductCarouselItem_viewer$key | null | undefined;
    item: ProductCarouselItem_item$key | null | undefined;
    photo: ProductCarouselItem_photos$key | null | undefined;
    afterDisplay: boolean;
    backgroundColor?: ImageBackgroundColorValue;
    disableLinks?: boolean;
    imageLoadVerticalOffset?: number;
    isEagerImageLoadEnabled: boolean;
    isHovering: boolean;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    photoIndex: number;
    size: TileSize;
    srcSet?: SrcSet;
};

export const ProductCarouselItem: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    photo: photoRef,
    afterDisplay,
    backgroundColor,
    disableLinks,
    isEagerImageLoadEnabled,
    imageLoadVerticalOffset,
    isHovering,
    onClick,
    onContentLoaded,
    photoIndex,
    size,
    srcSet,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const photo = useFragment(photoFragment, photoRef);

    const { hasApprovedVideo } = item || {};
    const isClient = useClientState();
    const imageId = useId();

    const content = (
        <ProductImage
            backgroundColor={backgroundColor}
            disableLinks={disableLinks}
            hasVideo={photoIndex === 0 && hasApprovedVideo}
            imageDraggable={false}
            imageLoadVerticalOffset={imageLoadVerticalOffset}
            item={item}
            isEagerImageLoadEnabled={isEagerImageLoadEnabled}
            onClick={onClick}
            onContentLoaded={onContentLoaded}
            photo={photo}
            size={size}
            srcSet={srcSet}
        />
    );

    return photoIndex === 0 && hasApprovedVideo && isClient ? (
        <Suspense
            fallback={afterDisplay ? <TileSpinner backgroundColor={backgroundColor} /> : content}
            key={`${imageId}-${photoIndex}-video`}
        >
            <LazyProductVideo
                viewer={viewer}
                item={item}
                backgroundColor={backgroundColor}
                isHovering={isHovering}
            >
                {content}
            </LazyProductVideo>
        </Suspense>
    ) : (
        content
    );
};
